import { create } from "zustand";

import enLocale from "@/locales/en.json";
import roLocale from "@/locales/ro.json";

export type Lang = "en" | "ro";

interface LangState {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggleLang: () => void;
}

function normalize(l: unknown): Lang {
  return l === "ro" ? "ro" : "en";
}

// Language preference lives in profiles.language (Supabase) and is hydrated
// by useAuth on sign-in. No localStorage cache — the previous value would
// leak across users on shared devices.
export const useLang = create<LangState>()((set, get) => ({
  lang: "en",
  setLang: (l) => set({ lang: normalize(l) }),
  toggleLang: () => set({ lang: get().lang === "en" ? "ro" : "en" }),
}));

// Best-effort cleanup of the legacy persisted key.
if (typeof window !== "undefined") {
  try {
    window.localStorage.removeItem("mp_lang");
  } catch {
    // ignore
  }
}


/**
 * All user-facing strings live in src/locales/{en,ro}.json.
 * The English source string passed to t(en) is the lookup key — the actual
 * displayed text is read from the matching locale file.
 */
type LocaleShape = {
  strings: Record<string, string>;
  countries: Record<string, string>;
};

const LOCALES: Record<Lang, LocaleShape> = {
  en: enLocale as LocaleShape,
  ro: roLocale as LocaleShape,
};

export function lookup(lang: Lang, key: string): string {
  const dict = LOCALES[lang].strings;
  if (key in dict) return dict[key];
  // Fallback to English file, then to the key itself.
  const en = LOCALES.en.strings;
  return en[key] ?? key;
}

function pick(lang: Lang, en: string, _pt?: string, ro?: string) {
  // Backward-compat: explicit ro override still wins so old call sites keep working.
  if (lang === "ro" && ro) return ro;
  return lookup(lang, en);
}

/**
 * Inline translation hook. Usage:
 *   const t = useT();
 *   <h1>{t("Predictions")}</h1>
 * The second (Portuguese) argument is kept for backward compatibility but
 * ignored — only English and Romanian are supported.
 */
export function useT() {
  const lang = useLang((s) => s.lang);
  return (en: string, _pt?: string, ro?: string) => pick(lang, en, _pt, ro);
}

/** For non-component code paths */
export function tx(en: string, _pt?: string, ro?: string): string {
  return pick(useLang.getState().lang, en, _pt, ro);
}

/** Translate a country/team name based on current language */
export function tCountry(name: string): string {
  const lang = useLang.getState().lang;
  return LOCALES[lang].countries[name] ?? name;
}

/** Hook variant that re-renders on language change */
export function useTCountry() {
  const lang = useLang((s) => s.lang);
  return (name: string) => LOCALES[lang].countries[name] ?? name;
}
